import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle, Building2, MapPin, Briefcase, UserPlus, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicRegister() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirmPassword: "" });

  useEffect(() => {
    fetchBusiness();
  }, [slug]);

  const fetchBusiness = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/business/${slug}`);
      if (res.ok) {
        setBusiness(await res.json());
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/register/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone || null, password: form.password }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Registration failed");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/50 via-white to-slate-50 p-4">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/50 via-white to-slate-50 p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Business Not Found</h2>
            <p className="text-slate-500 mb-6">The registration link is invalid or this business is no longer active.</p>
            <Link to="/login">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/50 via-white to-slate-50 p-4">
        <Card className="max-w-md w-full text-center" data-testid="registration-success">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Registration Successful!</h2>
            <p className="text-slate-500 mb-2">Your account has been created for</p>
            <p className="font-semibold text-amber-600 mb-4">{business.name}</p>
            <Badge className="bg-amber-100 text-amber-700 mb-6">Pending Admin Approval</Badge>
            <p className="text-sm text-slate-400 mb-6">An admin will review and approve your account. You'll be able to login once approved.</p>
            <Link to="/login">
              <Button className="bg-amber-500 hover:bg-amber-600">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/50 via-white to-slate-50 p-4">
      <Card className="max-w-lg w-full shadow-xl border-0" data-testid="public-register-card">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">{business.name}</CardTitle>
          <CardDescription className="flex items-center justify-center gap-3 mt-1 flex-wrap">
            {business.city && (
              <span className="flex items-center gap-1 text-sm">
                <MapPin className="w-3 h-3" /> {business.city}
              </span>
            )}
            {business.industry && (
              <span className="flex items-center gap-1 text-sm">
                <Briefcase className="w-3 h-3" /> {business.industry}
              </span>
            )}
          </CardDescription>
          <p className="text-sm text-slate-500 mt-3">Create your account to join this business</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reg-name">Full Name *</Label>
              <Input
                id="reg-name"
                data-testid="register-name-input"
                placeholder="Your full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="reg-email">Email *</Label>
              <Input
                id="reg-email"
                type="email"
                data-testid="register-email-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="reg-phone">Phone</Label>
              <Input
                id="reg-phone"
                data-testid="register-phone-input"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="reg-password">Password *</Label>
                <Input
                  id="reg-password"
                  type="password"
                  data-testid="register-password-input"
                  placeholder="Min 6 characters"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reg-confirm">Confirm Password *</Label>
                <Input
                  id="reg-confirm"
                  type="password"
                  data-testid="register-confirm-password-input"
                  placeholder="Re-enter password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-btn"
              className="w-full bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-900 h-11 text-base font-semibold shadow-lg"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              {submitting ? "Creating Account..." : "Create Account"}
            </Button>
            <p className="text-xs text-center text-slate-400 mt-2">
              Your account will require admin approval before you can login.
            </p>
          </form>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-amber-600 hover:underline">
              Already have an account? Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
